import { AlertCircle, Check, Upload } from 'lucide-react';

import { cn, formatDate } from '@/lib/utils';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

import { Activity } from './types';

type ActivityCardProps = {
    activities: Activity[];
    className?: string;
};

const ActivityCard = ({ activities, className }: ActivityCardProps) => {
    /**
     * Truncate filename intelligently - show start and end
     */
    const truncateFilename = (filename: string, maxLength: number = 35) => {
        if (filename.length <= maxLength) return filename;

        const ext = filename.split('.').pop();
        const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));

        if (nameWithoutExt.length <= maxLength - ext!.length - 4) {
            return filename;
        }

        const truncateLength = maxLength - ext!.length - 4;
        const start = nameWithoutExt.substring(
            0,
            Math.floor(truncateLength / 2),
        );
        const end = nameWithoutExt.substring(
            nameWithoutExt.length - Math.floor(truncateLength / 2),
        );

        return `${start}...${end}.${ext}`;
    };

    /**
     * Parse activity content and extract meaningful information
     */
    const parseActivity = (activity: Activity) => {
        const isAI = activity.type === 'ai_check';

        // Extract filename from title if present
        const filenameMatch = activity.title.match(/([^\/\\]+\.pdf)/i);
        const filename = filenameMatch ? filenameMatch[1] : null;

        switch (activity.type) {
            case 'upload':
                return {
                    icon: <Upload className="h-3.5 w-3.5" />,
                    badge: 'Uploaded',
                    badgeVariant: 'secondary' as const,
                    title: filename
                        ? truncateFilename(filename)
                        : 'File uploaded',
                    subtitle:
                        activity.description ||
                        `Uploaded by ${activity.username}`,
                    fullFilename: filename,
                };
            case 'status_change':
                const isVerified = activity.title
                    .toLowerCase()
                    .includes('verified');
                const requiresAttention = activity.title
                    .toLowerCase()
                    .includes('attention');

                return {
                    icon: isVerified ? (
                        <Check className="h-3.5 w-3.5" />
                    ) : (
                        <AlertCircle className="h-3.5 w-3.5" />
                    ),
                    badge: isVerified ? 'Verified' : 'Attention',
                    badgeVariant: isVerified
                        ? ('default' as const)
                        : ('destructive' as const),
                    title: filename
                        ? truncateFilename(filename)
                        : 'Status updated',
                    subtitle:
                        activity.description ||
                        (isVerified
                            ? 'Document verified'
                            : 'Requires attention'),
                    fullFilename: filename,
                };
            case 'ai_check':
                return {
                    icon: <Check className="h-3.5 w-3.5" />,
                    badge: 'AI Review',
                    badgeVariant: 'outline' as const,
                    title: filename
                        ? truncateFilename(filename)
                        : 'AI review completed',
                    subtitle:
                        activity.description || 'Automated review completed',
                    fullFilename: filename,
                };
            default:
                return {
                    icon: null,
                    badge: 'Activity',
                    badgeVariant: 'secondary' as const,
                    title: activity.title,
                    subtitle: activity.description || '',
                    fullFilename: null,
                };
        }
    };

    return (
        <Card className={className}>
            <CardHeader className="px-3 py-2">
                <CardTitle className="text-sm text-muted-foreground">
                    Recent Activity
                </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
                {activities && activities.length > 0 ? (
                    <div className="space-y-4 mt-4">
                        {activities.map((activity: Activity, index: number) => {
                            const isAI = activity.type === 'ai_check';
                            const config = parseActivity(activity);
                            const initials = activity.username
                                .split(' ')
                                .map((name) => name.charAt(0))
                                .join('')
                                .slice(0, 2)
                                .toUpperCase();

                            return (
                                <div
                                    key={index}
                                    className="flex items-start gap-3 hover:shadow-xl rounded-lg p-2 transition transition-all"
                                >
                                    <Avatar className="h-8 w-8 mt-1">
                                        <AvatarFallback
                                            className={cn(
                                                'text-xs font-medium',
                                                isAI
                                                    ? 'bg-purple-500 text-white'
                                                    : 'bg-muted text-muted-foreground',
                                            )}
                                        >
                                            {isAI ? 'AI' : initials}
                                        </AvatarFallback>
                                    </Avatar>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <Badge
                                                variant={config.badgeVariant}
                                                className="text-xs h-5 gap-1"
                                            >
                                                {config.icon}
                                                {config.badge}
                                            </Badge>
                                            <span className="text-xs text-muted-foreground">
                                                {formatDate(activity.createdAt)}
                                            </span>
                                        </div>

                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <p className="text-sm font-medium text-foreground truncate cursor-default">
                                                        {config.title}
                                                    </p>
                                                </TooltipTrigger>
                                                {config.fullFilename && (
                                                    <TooltipContent>
                                                        <p className="max-w-xs">
                                                            {
                                                                config.fullFilename
                                                            }
                                                        </p>
                                                    </TooltipContent>
                                                )}
                                            </Tooltip>
                                        </TooltipProvider>

                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {config.subtitle}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                        No recent activity
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

export default ActivityCard;
